/**
 * Um diálogo só para criar e para editar a campanha.
 *
 * São o mesmo formulário (dois campos, o mesmo schema, as mesmas mensagens) e o que muda é
 * o verbo — mesma decisão do `MonitorNameDialog`.
 *
 * O que NÃO tem aqui, e a ausência é a regra: **campo de código**. O `campaignCode` é
 * sorteado pelo servidor e não muda nunca. Ele aparece na criação como resultado, não como
 * pergunta — e o texto abaixo explica por quê, porque "de onde veio esse código?" é a
 * primeira dúvida de quem vê a tela.
 */
import { useEffect } from 'react';
import { Button, Dialog, Field, Input } from '@mgdlead/ui';

import { FormAlert } from '@mgdlead/ui';
import { campaignSchema } from '../../../lib/schemas';
import type { CampaignValues } from '../../../lib/schemas';
import { useZodForm } from '../../../lib/useZodForm';

export interface CampaignFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initialName?: string;
  initialDescription?: string;
  onSubmit: (values: { name: string; description: string }) => Promise<void>;
}

const HINT = {
  create:
    'Use o nome que você usaria para falar dela em voz alta: “Black Friday”, “Ofertas de setembro”. O código curto quem gera é o MGDLead.',
  edit: 'O nome muda quando você quiser. O código curto da campanha não — ele já pode estar num anúncio no ar.',
} as const;

export function CampaignFormDialog({
  open,
  onOpenChange,
  mode,
  initialName = '',
  initialDescription = '',
  onSubmit,
}: CampaignFormDialogProps) {
  const form = useZodForm({
    schema: campaignSchema,
    initialValues: {
      name: initialName,
      description: initialDescription,
    } satisfies CampaignValues,
    onSubmit: async (values) => {
      await onSubmit({ name: values.name, description: values.description });
      onOpenChange(false);
    },
  });

  const { reset } = form;
  // Reabrir com o texto da vez passada editaria a campanha errada.
  useEffect(() => {
    if (open) reset({ name: initialName, description: initialDescription });
  }, [open, initialName, initialDescription, reset]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === 'create' ? 'Nova campanha' : 'Editar campanha'}
      description={
        mode === 'create'
          ? 'A campanha junta os grupos que vão receber gente. Os grupos você vincula no passo seguinte.'
          : undefined
      }
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={form.pending}>
            Cancelar
          </Button>
          {/*
            O botão vive no rodapé do diálogo, fora do <form>. `form={FORM_ID}` é o que o
            liga de volta — sem isso ele vira decorativo e o Enter no campo passa a ser o
            único jeito de salvar.
          */}
          <Button
            variant="primary"
            loading={form.pending}
            loadingLabel="Salvando"
            form={FORM_ID}
            type="submit"
          >
            {mode === 'create' ? 'Criar campanha' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} noValidate onSubmit={form.handleSubmit} className="flex flex-col gap-3">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

        <Field label="Nome da campanha" hint={HINT[mode]} error={form.errors.name} required>
          {(control) => (
            <Input
              {...control}
              name="name"
              autoComplete="off"
              placeholder="Black Friday"
              value={form.values.name}
              invalid={form.errors.name !== undefined}
              onChange={(event) => form.setField('name', event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Descrição"
          hint="Opcional. Serve para você lembrar do que era esta campanha daqui a seis meses."
          error={form.errors.description}
        >
          {(control) => (
            <Input
              {...control}
              name="description"
              autoComplete="off"
              placeholder="Tráfego do Instagram para os grupos de ofertas"
              value={form.values.description}
              invalid={form.errors.description !== undefined}
              onChange={(event) => form.setField('description', event.target.value)}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}

const FORM_ID = 'campaign-form';
